import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface ISeries {
  key: string;
  label: string;
  colour: string;
}

export interface IMetricChart {
  /** Row per x value. Must contain the `xKey` plus one field per series key. */
  data: Array<Record<string, any>>;
  xKey: string;
  series: ISeries[];
  height?: number;
  variant?: 'line' | 'bar' | 'area';
  /** Stack bars/areas rather than drawing them side by side */
  stacked?: boolean;
  unit?: string;
  /** Formats Y axis ticks and tooltip values */
  format?: (value: number) => string;
  /** Formats the X axis tick labels */
  formatX?: (value: any) => string;
}

const AXIS = 'rgba(255,255,255,0.55)';
const GRID = 'rgba(255,255,255,0.10)';

const tooltipStyle = {
  background: 'rgba(18,20,26,0.95)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 4,
  fontSize: '0.8rem',
};

/**
 * Thin wrapper over recharts so the dashboard's charts share axis styling,
 * tooltip chrome and colour handling instead of repeating it per chart.
 */
const MetricChart = ({
  data,
  xKey,
  series,
  height = 240,
  variant = 'line',
  stacked = false,
  unit = '',
  format = (v: number) => String(Math.round(v * 100) / 100),
  formatX,
}: IMetricChart) => {
  if (!data || !data.length) {
    return (
      <div style={{ opacity: 0.6, fontSize: '0.85rem', padding: '18px 0' }}>
        No data for this range yet.
      </div>
    );
  }

  const common = {
    data,
    margin: { top: 8, right: 12, bottom: 4, left: 0 },
  };

  const axes = (
    <>
      <CartesianGrid stroke={GRID} vertical={false} />
      <XAxis
        dataKey={xKey}
        stroke={AXIS}
        tick={{ fontSize: 11, fill: AXIS }}
        tickFormatter={formatX}
        minTickGap={24}
      />
      <YAxis
        stroke={AXIS}
        tick={{ fontSize: 11, fill: AXIS }}
        tickFormatter={format}
        width={52}
        allowDecimals={false}
      />
      <Tooltip
        contentStyle={tooltipStyle}
        labelFormatter={(label) => (formatX ? formatX(label) : label)}
        formatter={(value: any, name: any) => [`${format(Number(value))}${unit ? ` ${unit}` : ''}`, name]}
      />
      <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
    </>
  );

  if (variant === 'bar') {
    return (
      <ResponsiveContainer width='100%' height={height}>
        <BarChart {...common}>
          {axes}
          {series.map(s => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.colour}
              stackId={stacked ? 'stack' : undefined}
              radius={[2, 2, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (variant === 'area') {
    return (
      <ResponsiveContainer width='100%' height={height}>
        <AreaChart {...common}>
          {axes}
          {series.map(s => (
            <Area
              key={s.key}
              type='monotone'
              dataKey={s.key}
              name={s.label}
              stroke={s.colour}
              fill={s.colour}
              fillOpacity={0.22}
              stackId={stacked ? 'stack' : undefined}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width='100%' height={height}>
      <LineChart {...common}>
        {axes}
        {series.map(s => (
          <Line
            key={s.key}
            type='monotone'
            dataKey={s.key}
            name={s.label}
            stroke={s.colour}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default MetricChart;
